// SPDX-License-Identifier: AGPL-3.0-or-later
export class ExpressionError extends Error {
    pos;
    constructor(message, pos) {
        super(message);
        this.pos = pos;
        this.name = 'ExpressionError';
    }
}
/** Whitelist of identifiers permitted by the language. Anything else fails parse. */
const WHITELISTED_FUNCTIONS = new Set(['answer', 'count']);
const WHITELISTED_LITERAL_KEYWORDS = new Set(['true', 'false', 'null']);
export function tokenise(source) {
    const tokens = [];
    let i = 0;
    while (i < source.length) {
        const ch = source[i];
        if (ch === undefined)
            break;
        // Whitespace
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i += 1;
            continue;
        }
        // Single-char punctuation
        if (ch === '(') {
            tokens.push({ kind: 'LPAREN', value: ch, pos: i });
            i += 1;
            continue;
        }
        if (ch === ')') {
            tokens.push({ kind: 'RPAREN', value: ch, pos: i });
            i += 1;
            continue;
        }
        if (ch === ',') {
            tokens.push({ kind: 'COMMA', value: ch, pos: i });
            i += 1;
            continue;
        }
        // Operators (two-char first)
        const two = source.slice(i, i + 2);
        if (two === '==') {
            tokens.push({ kind: 'OP_EQ', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '!=') {
            tokens.push({ kind: 'OP_NE', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '<=') {
            tokens.push({ kind: 'OP_LE', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '>=') {
            tokens.push({ kind: 'OP_GE', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '&&') {
            tokens.push({ kind: 'OP_AND', value: two, pos: i });
            i += 2;
            continue;
        }
        if (two === '||') {
            tokens.push({ kind: 'OP_OR', value: two, pos: i });
            i += 2;
            continue;
        }
        if (ch === '<') {
            tokens.push({ kind: 'OP_LT', value: ch, pos: i });
            i += 1;
            continue;
        }
        if (ch === '>') {
            tokens.push({ kind: 'OP_GT', value: ch, pos: i });
            i += 1;
            continue;
        }
        if (ch === '!') {
            tokens.push({ kind: 'OP_NOT', value: ch, pos: i });
            i += 1;
            continue;
        }
        // String literal: single or double quote, no escapes, no newlines.
        if (ch === "'" || ch === '"') {
            const quote = ch;
            const start = i;
            i += 1;
            let value = '';
            while (i < source.length && source[i] !== quote) {
                const c = source[i];
                if (c === undefined || c === '\n' || c === '\r' || c === '\\') {
                    throw new ExpressionError(`unterminated or unsupported string literal starting at position ${start}`, start);
                }
                value += c;
                i += 1;
            }
            if (source[i] !== quote) {
                throw new ExpressionError(`unterminated string literal at position ${start}`, start);
            }
            i += 1; // closing quote
            tokens.push({ kind: 'STRING', value, pos: start });
            continue;
        }
        // Number literal: optional minus, integer with optional fraction. No exponent.
        if (ch === '-' || (ch >= '0' && ch <= '9')) {
            const start = i;
            if (ch === '-')
                i += 1;
            let saw = false;
            while (i < source.length) {
                const c = source[i];
                if (c === undefined)
                    break;
                if (c < '0' || c > '9')
                    break;
                i += 1;
                saw = true;
            }
            if (source[i] === '.') {
                i += 1;
                let frac = false;
                while (i < source.length) {
                    const c = source[i];
                    if (c === undefined)
                        break;
                    if (c < '0' || c > '9')
                        break;
                    i += 1;
                    frac = true;
                }
                if (!frac) {
                    throw new ExpressionError(`malformed number at position ${start}`, start);
                }
            }
            if (!saw) {
                // A lone '-' is not a valid token; only inside numbers.
                throw new ExpressionError(`unexpected character '-' at position ${start}`, start);
            }
            tokens.push({ kind: 'NUMBER', value: source.slice(start, i), pos: start });
            continue;
        }
        // Identifier: [a-zA-Z_][a-zA-Z0-9_]*. Only whitelisted idents are accepted later.
        if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
            const start = i;
            while (i < source.length) {
                const c = source[i];
                if (c === undefined)
                    break;
                const isIdent = (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || (c >= '0' && c <= '9') || c === '_';
                if (!isIdent)
                    break;
                i += 1;
            }
            tokens.push({ kind: 'IDENT', value: source.slice(start, i), pos: start });
            continue;
        }
        throw new ExpressionError(`unexpected character ${JSON.stringify(ch)} at position ${i}`, i);
    }
    tokens.push({ kind: 'EOF', value: '', pos: source.length });
    return tokens;
}
// --- Parser --------------------------------------------------------------
class Parser {
    tokens;
    pos = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parse() {
        const expr = this.parseOr();
        this.expect('EOF', 'expected end of expression');
        return expr;
    }
    peek() {
        const t = this.tokens[this.pos];
        if (t === undefined) {
            // Should be impossible — tokenise always appends EOF.
            throw new ExpressionError('unexpected end of token stream', -1);
        }
        return t;
    }
    advance() {
        const t = this.peek();
        this.pos += 1;
        return t;
    }
    expect(kind, msg) {
        const t = this.peek();
        if (t.kind !== kind) {
            throw new ExpressionError(`${msg} (got ${t.kind} '${t.value}')`, t.pos);
        }
        return this.advance();
    }
    parseOr() {
        let left = this.parseAnd();
        while (this.peek().kind === 'OP_OR') {
            this.advance();
            const right = this.parseAnd();
            left = { kind: 'logical', op: '||', left, right };
        }
        return left;
    }
    parseAnd() {
        let left = this.parseNot();
        while (this.peek().kind === 'OP_AND') {
            this.advance();
            const right = this.parseNot();
            left = { kind: 'logical', op: '&&', left, right };
        }
        return left;
    }
    parseNot() {
        if (this.peek().kind === 'OP_NOT') {
            this.advance();
            const child = this.parseNot();
            return { kind: 'not', child };
        }
        return this.parseCmp();
    }
    parseCmp() {
        const left = this.parsePrimary();
        const next = this.peek();
        const map = {
            OP_EQ: '==',
            OP_NE: '!=',
            OP_LT: '<',
            OP_LE: '<=',
            OP_GT: '>',
            OP_GE: '>=',
        };
        const op = map[next.kind];
        if (op !== undefined) {
            this.advance();
            const right = this.parsePrimary();
            return { kind: 'binary', op, left, right };
        }
        return left;
    }
    parsePrimary() {
        const t = this.peek();
        if (t.kind === 'LPAREN') {
            this.advance();
            const inner = this.parseOr();
            this.expect('RPAREN', "expected ')'");
            return inner;
        }
        if (t.kind === 'STRING') {
            this.advance();
            return { kind: 'literal', value: t.value };
        }
        if (t.kind === 'NUMBER') {
            this.advance();
            return { kind: 'literal', value: Number(t.value) };
        }
        if (t.kind === 'IDENT') {
            if (WHITELISTED_LITERAL_KEYWORDS.has(t.value)) {
                this.advance();
                if (t.value === 'true')
                    return { kind: 'literal', value: true };
                if (t.value === 'false')
                    return { kind: 'literal', value: false };
                return { kind: 'literal', value: null };
            }
            if (!WHITELISTED_FUNCTIONS.has(t.value)) {
                throw new ExpressionError(`identifier '${t.value}' is not whitelisted (allowed: answer, count, true, false, null)`, t.pos);
            }
            const name = t.value;
            this.advance();
            this.expect('LPAREN', `expected '(' after function '${name}'`);
            const argTok = this.expect('STRING', `function '${name}' requires a single string argument`);
            this.expect('RPAREN', `expected ')' closing '${name}('`);
            return { kind: 'call', name, arg: argTok.value };
        }
        throw new ExpressionError(`unexpected token ${t.kind} '${t.value}'`, t.pos);
    }
}
export function parse(source) {
    return new Parser(tokenise(source)).parse();
}
export function evaluate(ast, env) {
    switch (ast.kind) {
        case 'literal':
            return ast.value;
        case 'call': {
            const result = ast.name === 'answer' ? env.answer(ast.arg) : env.count(ast.arg);
            return result === undefined ? null : result;
        }
        case 'not': {
            const v = evaluate(ast.child, env);
            return !isTruthy(v);
        }
        case 'logical': {
            const l = evaluate(ast.left, env);
            if (ast.op === '&&')
                return isTruthy(l) ? evaluate(ast.right, env) : l;
            // ||
            return isTruthy(l) ? l : evaluate(ast.right, env);
        }
        case 'binary': {
            const l = evaluate(ast.left, env);
            const r = evaluate(ast.right, env);
            switch (ast.op) {
                case '==':
                    return strictEqual(l, r);
                case '!=':
                    return !strictEqual(l, r);
                case '<':
                    return compareNumeric(l, r) < 0;
                case '<=':
                    return compareNumeric(l, r) <= 0;
                case '>':
                    return compareNumeric(l, r) > 0;
                case '>=':
                    return compareNumeric(l, r) >= 0;
            }
        }
    }
}
function isTruthy(v) {
    if (v === null || v === undefined)
        return false;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v !== 0 && !Number.isNaN(v);
    if (typeof v === 'string')
        return v.length > 0;
    return Boolean(v);
}
function strictEqual(a, b) {
    // Treat null and undefined as the same "absent" sentinel for forgiving comparisons.
    const na = a === undefined ? null : a;
    const nb = b === undefined ? null : b;
    return na === nb;
}
function compareNumeric(a, b) {
    const na = toNumber(a);
    const nb = toNumber(b);
    if (Number.isNaN(na) || Number.isNaN(nb))
        return Number.NaN;
    if (na < nb)
        return -1;
    if (na > nb)
        return 1;
    return 0;
}
function toNumber(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    if (v === null || v === undefined)
        return Number.NaN;
    if (typeof v === 'string') {
        if (v.trim() === '')
            return Number.NaN;
        const n = Number(v);
        return n;
    }
    return Number.NaN;
}
//# sourceMappingURL=expression.js.map